// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Request types
export interface EarlyInterestRequest {
    email: string;
    zipCode: string;
    interestType: 'NEED_HELP' | 'WANT_TO_VOLUNTEER' | 'CARE_PROFESSIONAL';
    serviceInterests?: string[];
    yearsExperience?: string;
    isFlexible?: boolean;

    // Enhanced data collection fields
    frequencyNeed?: 'weekly' | 'bi_weekly' | 'monthly' | 'as_needed' | 'unsure';
    timelineNeed?: 'immediately' | 'within_month' | 'within_3_months' | 'planning_ahead';
    primaryConcerns?: string[];
    availabilityHours?: 'flexible' | '2_5_hours' | '5_10_hours' | '10_plus_hours';
    availabilityTimes?: string[];
    hasVehicle?: boolean;
    currentClientVolume?: 'just_starting' | 'part_time' | 'full_time' | 'expanding';
}

export interface ContactFormRequest {
    name: string;
    email: string;
    subject: string;
    message: string;
}

export interface GeographicExpansionRequest {
    email: string;
    city: string;
    state: string;
    zipCode?: string;
    interestLevel: 'very_interested' | 'somewhat_interested' | 'just_curious';
    comments?: string;
}

// Response types
export interface ApiResponse<T = any> {
    success: boolean;
    message: string;
    data?: T;
    timestamp: string;
}

export interface ApiError {
    error: string;
    message: string;
    details?: any;
    timestamp: string;
}

// API Service Class
class ApiService {
    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const url = `${API_BASE_URL}${endpoint}`;

        const config: RequestInit = {
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            ...options,
        };

        try {
            const response = await fetch(url, config);
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
            }

            return data;
        } catch (error) {
            console.error(`API Error (${endpoint}):`, error);
            throw error;
        }
    }

    // Generic GET method
    async get<T>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint, { method: 'GET' });
    }

    // Generic POST method
    async post<T>(endpoint: string, data: any): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // Submit early interest signup (legacy)
    async submitEarlyInterest(data: EarlyInterestRequest): Promise<ApiResponse> {
        return this.request<ApiResponse>('/early-interest', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // Submit contact form (legacy)
    async submitContactForm(data: ContactFormRequest): Promise<ApiResponse> {
        return this.request<ApiResponse>('/contact', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // Submit geographic expansion interest (legacy)
    async submitGeographicExpansionInterest(data: GeographicExpansionRequest): Promise<ApiResponse> {
        return this.request<ApiResponse>('/geographic-expansion-interest', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // Submit interest (using existing early-interest endpoint)
    async submitInterest(data: {
        email: string;
        zipCode: string;
        interestType: 'NEED_HELP' | 'WANT_TO_VOLUNTEER' | 'CARE_PROFESSIONAL';
        details?: Record<string, any>;
    }): Promise<ApiResponse> {
        const submissionData = {
            email: data.email,
            zipCode: data.zipCode,
            interestType: data.interestType,
            // Map common details fields to legacy structure
            serviceInterests: data.details?.serviceInterests || [],
            yearsExperience: data.details?.yearsExperience || '',
            isFlexible: data.details?.isFlexible || false
        };

        return this.request<ApiResponse>('/early-interest', {
            method: 'POST',
            body: JSON.stringify(submissionData),
        });
    }

    // Get early interest statistics (for admin/analytics)
    async getEarlyInterestStats(): Promise<ApiResponse> {
        return this.request<ApiResponse>('/early-interest/stats');
    }

    // Health check
    async healthCheck(): Promise<any> {
        return this.request('/health');
    }
}

// Export singleton instance
export const apiService = new ApiService();

// Export individual methods for easier imports
export const {
    submitEarlyInterest,
    submitContactForm,
    submitGeographicExpansionInterest,
    submitInterest,
    getEarlyInterestStats,
    healthCheck,
} = apiService;
